/**
 * API Client Layer
 *
 * Provides authenticated openapi-fetch clients for the admin portal.
 *
 * Architecture:
 * - Typed clients use openapi-fetch with auth middleware for spec'd endpoints
 * - Legacy ApiClient retained for non-spec'd endpoints (dashboard, loans, etc.)
 * - Auth middleware injects JWT from zustand store and handles 401 logout
 *
 * All API calls route through the Vite proxy at /api -> platform-admin-api (port 9302).
 */

import createClient from 'openapi-fetch';
import type { paths as PlatformAdminPaths } from '@aaimplatform/institutional-types/platform-admin';
import type { paths as IdentityProviderRegistryPaths } from '@aaimplatform/institutional-types/identity-provider-registry';
import type { paths as AuditTrailPaths } from '@aaimplatform/institutional-types/audit-trail';
import type { paths as PropertyTitleAdminPaths } from '@aaimplatform/institutional-types/property-title-admin';
import type { paths as ProviderRateLimitPaths } from '@aaimplatform/institutional-types/provider-rate-limits';
import type { paths as CompliancePaths } from '@aaimplatform/institutional-types/compliance-service';
import type { paths as TenantWebhookConfigPaths } from '@aaimplatform/institutional-types/tenant-webhook-config';
import type { paths as WebhookDeliveryLogPaths } from '@aaimplatform/institutional-types/webhook-delivery-log';
import type { paths as LoanSyndicationProtocolPaths } from '@aaimplatform/institutional-types/loan-syndication-protocol';
import type { paths as CooperativeReserveTokenPaths } from '@aaimplatform/institutional-types/generated/cooperative-reserve-token';
import type { paths as RegulatoryReadinessPaths } from '@aaimplatform/institutional-types/regulatory-readiness';
import type { paths as UccFilingLifecyclePaths } from '@aaimplatform/institutional-types/ucc-filing-lifecycle';
import type { paths as UccJurisdictionRulesPaths } from '@aaimplatform/institutional-types/ucc-jurisdiction-rules';
import type { paths as UccFeeReconciliationPaths } from '@aaimplatform/institutional-types/ucc-fee-reconciliation';
import type { paths as FiStaffLifecyclePaths } from '@aaimplatform/institutional-types/fi-staff-lifecycle';
import type { paths as AuthStepUpPaths } from '@aaimplatform/institutional-types/auth-step-up';
import type { paths as AuthTotpPaths } from '@aaimplatform/institutional-types/auth-totp';
import type { paths as UserManagementRbacPaths } from '@aaimplatform/institutional-types/user-management-rbac';
import type { paths as AbacPoliciesPaths } from '@aaimplatform/institutional-types/abac-policies';
import type { paths as SupportPlatformPaths } from '@aaimplatform/institutional-types/support-platform';
import type { paths as ExaminerEvidencePackagePaths } from '@aaimplatform/institutional-types/examiner-evidence-package';
import type { paths as CoreDepositFeedPaths } from '@aaimplatform/institutional-types/generated/core-deposit-feed';
import type { Middleware } from 'openapi-fetch';
import { createMemberLiquidityClient } from '@aaimplatform/institutional-types/api-clients';
import { createEconomicModelingClient } from '@aaimplatform/institutional-types/api-clients';
import { useAuthStore } from '../stores/auth';
import { csrfHeaders, readCsrfCookie } from '../lib/csrf';

// ---------------------------------------------------------------------------
// Auth-broken detection -- shared between openapi-fetch middleware + legacy ApiClient
// ---------------------------------------------------------------------------

/**
 * Detect responses that indicate the user's auth state is broken:
 * - 401 that SURVIVED a silent-refresh attempt (expired session, no/invalid
 *   refresh token) -- always auth-broken, NOT suppressed under VITE_BYPASS_AUTH
 *   (dev-bypass does not cover the aaim-core-proxied staff/idp/abac surfaces).
 * - 400 with `code: 'TENANT_SCOPE_REQUIRED'` (JWT decoded but missing tenantId claim,
 *   typically a stale token from before a schema change or a partial dev session)
 * - 400 with `code: 'AUTH_REQUIRED'` (server rejected before tenant context lookup)
 */
/**
 * Silent cookie refresh. On a 401, ask aaim-core to rotate the HttpOnly session
 * cookies via POST /api/v1/auth/refresh (credentials: 'include' carries the
 * 'refresh-token' cookie). Returns true when the rotation succeeded, false when
 * refresh is impossible/failed (no/expired refresh cookie). No bearer token is
 * read or stored -- the credential lives only in the cookie (SEC-V04).
 *
 * Guarded by an in-flight promise so a burst of concurrent 401s (every page
 * widget firing its query at once -- the exact symptom this fixes) triggers a
 * SINGLE refresh, not one per request.
 */
let inFlightRefresh: Promise<boolean> | null = null;

async function attemptTokenRefresh(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async (): Promise<boolean> => {
    try {
      // Cookie-only refresh (N6, SEC-V04): the HttpOnly 'refresh-token' cookie
      // carries the credential (credentials: 'include'); the backend rotates
      // the session cookies in-place. We only need to know it succeeded -- no
      // token is read from or written to JS-readable state.
      // See .claude/rules/no-browser-token-storage.md.
      // The refresh-token cookie authenticates this POST, so it echoes the
      // csrf-token cookie as X-CSRF-Token: aaim-core refuses a cookie refresh
      // without it (403 CSRF_REJECTED, GH #4888).
      const res = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        credentials: 'include',
        body: JSON.stringify({ type: 'jwt' }),
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

async function isAuthBrokenResponse(response: Response): Promise<boolean> {
  if (response.status === 401) {
    // A 401 that SURVIVED a refresh attempt is genuinely auth-broken and MUST
    // bounce -- even under VITE_BYPASS_AUTH. The prior `!BYPASS_AUTH` swallow
    // masked real aaim-core 401s on the proxied staff/idp/abac surfaces (which
    // have no dev bypass), leaving components to fail piecemeal with no
    // redirect. Bypass only suppresses the platform-admin-api-served pages,
    // never the aaim-core-proxied ones.
    return true;
  }
  if (response.status === 400) {
    try {
      const body = (await response.clone().json()) as
        | { error?: { code?: string }; code?: string }
        | undefined;
      const code = body?.error?.code ?? body?.code;
      return code === 'TENANT_SCOPE_REQUIRED' || code === 'AUTH_REQUIRED';
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Clear auth state and redirect to /login. Idempotent -- if already on /login,
 * does nothing (avoids redirect loops on /login itself returning 401).
 */
function bounceToLogin(): void {
  // Sprint C10 follow-on: do NOT bounce while on /login or /auth/callback.
  // - /login bounce would loop trivially.
  // - /auth/callback is mid-OAuth flow; the page is about to call
  //   loginWithTokens() and navigate to dashboard. Bouncing during the brief
  //   pre-store-update window would re-launch the OAuth flow and create an
  //   infinite redirect cycle (callback -> 400 -> bounce -> callback).
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (path.startsWith('/login') || path.startsWith('/auth/callback')) {
    return;
  }
  useAuthStore.getState().logout();
  window.location.href = '/login';
}

// ---------------------------------------------------------------------------
// CSRF double-submit (N8) -- echo the csrf-token cookie as X-CSRF-Token
// ---------------------------------------------------------------------------

/** Methods that mutate state and therefore carry the CSRF header. */
const CSRF_MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// readCsrfCookie (lib/csrf.ts) reads the NON-HttpOnly `csrf-token` cookie the
// session issuer set alongside the HttpOnly session cookie; the SPA echoes it
// as the `X-CSRF-Token` header on mutating requests, and a backend that
// enforces the double-submit match refuses a cookie-authenticated mutation
// without it. It returns null when the cookie is absent. The reader lives in
// lib/csrf.ts so stores/auth.ts, which this module imports, can use it too.

// ---------------------------------------------------------------------------
// Auth middleware -- shared across all openapi-fetch clients
// ---------------------------------------------------------------------------

/**
 * openapi-fetch middleware that bounces to /login on auth-broken responses
 * (401, 400 TENANT_SCOPE_REQUIRED, 400 AUTH_REQUIRED). See
 * `isAuthBrokenResponse` for full criteria.
 *
 * N6 cookie cutover (SEC-V04): there is NO onRequest bearer attach. Every
 * client is created with `credentials: 'include'`, so the HttpOnly session
 * cookie authenticates each request and the SPA holds no token. The
 * @aaimplatform/database cookie->Bearer shim reads the cookie server-side.
 * See .claude/rules/no-browser-token-storage.md.
 */
/**
 * Pre-dispatch copies of in-flight requests, keyed by the Request object
 * openapi-fetch is about to send (GH #2461).
 *
 * WHY A CLONE TAKEN IN onRequest, AND NOT IN onResponse. By the time
 * `onResponse` runs the Request has already been dispatched, so its body stream
 * is consumed and `bodyUsed` is true. Per the Fetch standard `Request.clone()`
 * THROWS a TypeError once the body is used or disturbed -- and that TypeError
 * escaped the middleware, past unwrap(), into the TanStack mutation, and
 * rendered to an FI reviewer as "VERIFICATION FAILED / Failed to execute
 * 'clone' on 'Request': Request body is already used" while declining a pledge.
 * `onRequest` is the last hook that still sees an unconsumed body.
 *
 * WHY THIS SURVIVED. `clone()` on a BODYLESS request succeeds, and a GET never
 * sets `bodyUsed`. So the silent refresh-and-retry worked exactly as designed
 * for reads -- the symptom it was built for -- while every POST/PUT/PATCH/DELETE
 * that 401'd and then successfully refreshed threw instead of retrying. Half
 * broken along an axis nothing exercised, which is why the retry is now pinned
 * by a test with a body.
 *
 * WHY A WeakMap AND NOT A MODULE-SCOPED SLOT. `attemptTokenRefresh` exists
 * precisely because concurrent 401s are the normal case (every page widget
 * firing at once), so a single shared slot would hand one request's body to
 * another. Keying on the Request object keeps them separate, and WeakMap means
 * a request that never reaches onResponse is collected rather than leaked.
 */
const retryableRequests = new WeakMap<Request, Request>();

/**
 * The step-up mint, whose 401 means TWO different things.
 *
 * `schema-registry/auth-step-up.openapi.yaml:86-87` declares it outright:
 * "Bearer missing or invalid; OR proof verification failed". A wrong password
 * and an expired access cookie are the same status code, so the generic
 * refresh-retry cannot tell them apart. Retrying a rejected proof replays it
 * against the endpoint's own brute-force limiter (:98-99), and the
 * refresh-FAILURE branch is worse still: it calls bounceToLogin(), so mistyping
 * a password while the refresh cookie happened to be stale logged the operator
 * out.
 *
 * So this route is excluded from the refresh-retry entirely and its 401 is
 * returned to the caller untouched, letting the modal render the server's own
 * error instead of a session-recovery side effect. Narrowing the 401 at the
 * contract layer would let any client discriminate without route-sniffing; that
 * is a spec change and an operator call (GH #2461 deliverable 6).
 */
const STEP_UP_MINT_PATH = '/api/v1/auth/step-up-token';

function isStepUpMintRequest(request: Request): boolean {
  try {
    const base =
      typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost';
    return new URL(request.url, base).pathname.endsWith(STEP_UP_MINT_PATH);
  } catch {
    return request.url.includes(STEP_UP_MINT_PATH);
  }
}

/**
 * CSRF-only middleware, for openapi-fetch clients that are NOT the main
 * admin-portal client and therefore must not inherit `authMiddleware`'s 401
 * refresh-retry machinery (GH #2461).
 *
 * Mirrors `apps/advisor-tools/src/api/client.ts:113-121` exactly. Added
 * 2026-08-20 with GH #2653: mounting `enforceCsrf: true` on pe-valuation makes
 * every cookie-authenticated mutation require the double-submit header, and
 * `pages/pe/CollateralMonitoring.tsx` builds its own pe-valuation client that
 * echoed nothing. Its two POSTs (/pe/valuation/collateral-assessment and
 * /pe/valuation/stress-test) would 403 without this.
 *
 * The session cookie itself rides via `credentials: 'include'`, defaulted in
 * the createClient wrapper.
 */
export const csrfMiddleware: Middleware = {
  onRequest({ request }) {
    if (CSRF_MUTATING_METHODS.has(request.method.toUpperCase())) {
      const csrf = readCsrfCookie();
      if (csrf) request.headers.set('X-CSRF-Token', csrf);
    }
    return request;
  },
};

export const authMiddleware: Middleware = {
  onRequest({ request }) {
    // CSRF double-submit (N8): on mutating methods, echo the non-HttpOnly
    // csrf-token cookie as X-CSRF-Token. The @aaimplatform/database auth
    // middleware compares the two for cookie-authenticated requests. Safe
    // methods (GET/HEAD/OPTIONS) carry no header. No-op when the cookie is
    // absent (pre-rollout) so this change is backward-compatible.
    if (CSRF_MUTATING_METHODS.has(request.method.toUpperCase())) {
      const csrf = readCsrfCookie();
      if (csrf) request.headers.set('X-CSRF-Token', csrf);
    }
    // Capture the retryable copy while the body is still unconsumed (GH #2461).
    // Best-effort: a clone failure here must never break the request itself, so
    // the retry simply becomes unavailable and onResponse falls through to the
    // bounce it would have reached anyway.
    try {
      retryableRequests.set(request, request.clone());
    } catch {
      /* not retryable; onResponse degrades to no-retry rather than throwing */
    }
    return request;
  },
  async onResponse({ request, response }) {
    // On a 401, try one cookie-based silent refresh + retry of the original
    // request before declaring the session broken. The retry reuses the
    // pre-dispatch copy (which already carries credentials: 'include'), so the
    // freshly-rotated session cookie rides it. This makes the access-cookie
    // expiry invisible to the user as long as the 7d refresh cookie is valid.
    if (response.status === 401) {
      // The step-up mint's 401 is overloaded (see STEP_UP_MINT_PATH). Neither
      // refresh-retry nor bounce is correct here -- hand the server's own 401
      // back so the caller can render a failed proof AS a failed proof.
      if (isStepUpMintRequest(request)) {
        return response;
      }

      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        // Prefer the pre-dispatch copy. Fall back to cloning here only when the
        // body was never consumed (a GET), which is the case that always worked.
        // If neither is available we must NOT throw -- a browser internal
        // surfacing in the UI is the whole defect -- so we fall through to the
        // same bounce path an un-retryable 401 would have taken.
        let retrySource = retryableRequests.get(request) ?? null;
        if (!retrySource && !request.bodyUsed) {
          try {
            retrySource = request.clone();
          } catch {
            retrySource = null;
          }
        }

        if (retrySource) {
          // The refresh re-minted the csrf-token cookie, so the copy's
          // X-CSRF-Token (read before dispatch) no longer matches it. Echo the
          // current value, or an enforcing backend refuses the retried
          // mutation with 403 CSRF_REJECTED (GH #4888).
          if (CSRF_MUTATING_METHODS.has(retrySource.method.toUpperCase())) {
            const csrf = readCsrfCookie();
            if (csrf) retrySource.headers.set('X-CSRF-Token', csrf);
          }
          const retryResponse = await fetch(retrySource);
          // If the retry still 401s, the refresh did not actually fix auth ->
          // fall through to the bounce below with the retry's response.
          if (retryResponse.status !== 401) {
            return retryResponse;
          }
          if (await isAuthBrokenResponse(retryResponse)) {
            bounceToLogin();
          }
          return retryResponse;
        }
      }
      // Refresh failed (e.g. the 7d refresh cookie expired), or the request was
      // not retryable: one clean redirect to /login instead of piecemeal
      // component failures.
      bounceToLogin();
      return response;
    }
    if (await isAuthBrokenResponse(response)) {
      bounceToLogin();
    }
    return response;
  },
};

// ---------------------------------------------------------------------------
// Typed clients for spec'd endpoints
// ---------------------------------------------------------------------------

/**
 * Platform Admin client (tenants, integrations, data-providers, health, settings, modeling).
 * BaseUrl /api/admin routes through Vite proxy to platform-admin-api.
 */
function createAdminPlatformClient() {
  const client = createClient<PlatformAdminPaths>({ baseUrl: '/api/admin', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Platform Admin client for the spec's absolute /api/v1/admin/... keys. A new
 * platform-admin-api endpoint lands canonical (api-version-canonical.md D2,
 * D3) under an absolute key while the legacy keys stay relative to /api/admin,
 * so this client anchors at the origin and calls the generated key as written
 * (the tenant-sla.ts and deal-alerts.ts precedent). The Vite proxy and nginx
 * send /api/v1/admin/* to platform-admin-api through their /api catch-alls.
 */
function createAdminPlatformCanonicalClient() {
  const client = createClient<PlatformAdminPaths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Identity Provider Registry client (platform-admin-api proxy to aaim-core identity-provider-registry).
 * Spec paths declare the full /api/v1/identity-providers/* prefix (post the
 * api-version-canonical sweep), so baseUrl is empty. The Vite proxy
 * (vite.config.ts) and the Docker nginx.conf both rewrite
 * /api/v1/identity-providers/* -> /api/admin/identity-providers/* on
 * platform-admin-api (port 9302), which proxies to the aaim-core
 * identity-provider-registry service. Mirrors the regulatoryReadinessClient,
 * loanSyndicationClient, cooperativeReserveTokenClient pattern.
 */
function createAdminIdentityProviderClient() {
  const client = createClient<IdentityProviderRegistryPaths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Audit Trail client (audit logs, events, integrity verification).
 * Paths: /audit/logs, /audit/events -- prefixed by /api/admin via baseUrl.
 */
function createAdminAuditClient() {
  const client = createClient<AuditTrailPaths>({ baseUrl: '/api/admin', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Property Title Admin client (Property Valuations, Property Equity,
 * Title Searches at canonical mount, Lien Verifications, ATTOM provider
 * diagnostics).
 * Canonical mount in platform-admin-api/src/server.ts is /api/v1/property/...,
 * so baseUrl is /api/v1 and spec paths declare the remaining segments
 * (/property/valuations, /property/equity, /title/searches, etc.).
 */
function createPropertyTitleAdminClient(baseUrl: string = '/api/v1') {
  const client = createClient<PropertyTitleAdminPaths>({ baseUrl, credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Provider Rate Limits client.
 * Paths: /provider-rate-limits/* -- prefixed by /api/admin via baseUrl.
 */
function createAdminProviderRateLimitsClient() {
  const client = createClient<ProviderRateLimitPaths>({ baseUrl: '/api/admin', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Compliance Service client.
 * Paths: /compliance/* -- prefixed by /api/admin via baseUrl.
 */
function createAdminComplianceClient() {
  const client = createClient<CompliancePaths>({ baseUrl: '/api/admin', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Tenant Webhook Config client (Sprint C14 Lane C WP-5).
 * Paths: /tenants/me/webhook-endpoints[/...] prefixed by /api/admin via baseUrl.
 * Mirrors the /api/admin/tenants/me/webhook-endpoints mount in
 * platform-admin-api/src/server.ts.
 */
function createAdminTenantWebhookConfigClient() {
  const client = createClient<TenantWebhookConfigPaths>({ baseUrl: '/api/admin', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Webhook Delivery Log client (Sprint C14 Lane C WP C-2 / Lane B WP B-4).
 * Paths: /webhook-deliveries[, /:id, /health] prefixed by /api/admin via baseUrl.
 * Mirrors the /api/admin/webhook-deliveries mount in
 * platform-admin-api/src/server.ts.
 */
function createAdminWebhookDeliveryLogClient() {
  const client = createClient<WebhookDeliveryLogPaths>({ baseUrl: '/api/admin', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Loan Syndication Protocol client (Sprint C14 Lane B WP B-6/B-7).
 * Spec paths declare the full /api/v1/syndication/* prefix, so baseUrl
 * is empty. The Vite proxy forwards /api/v1/syndication/* to port 9570
 * (see vite.config.ts) without rewrite, matching the canonical
 * regulatoryReadinessClient and platform-admin loanSyndicationClient
 * patterns post-C13 WP-5 baseUrl fix.
 *
 * For FI-tenant operator views, the C-3 marketplace masking applies
 * server-side: leadParticipantId is replaced with an opaque token
 * unless `disclosedLeadArranger` is true; counterparty entries in
 * commitment lists are masked except the caller's own commit. The
 * SPA must NOT attempt to unmask -- it consumes the masked shape and
 * surfaces `disclosedLeadArranger` / `counterpartyCount` for context.
 */
function createAdminLoanSyndicationClient() {
  const client = createClient<LoanSyndicationProtocolPaths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Cooperative Reserve Token client (Sprint C14 Lane B WP B-7).
 * Spec paths declare the full /api/v1/crt/* prefix, so baseUrl is
 * empty. The Vite proxy forwards /api/v1/crt/* to port 9571 (see
 * vite.config.ts) without rewrite. Used by RiskCapital.tsx for the
 * step-on (mint) / step-off (burn) flows and the FI's CRT balance
 * view via /api/v1/crt/positions/{participantId}.
 */
function createAdminCooperativeReserveTokenClient() {
  const client = createClient<CooperativeReserveTokenPaths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Regulatory Readiness client.
 * Spec paths declare the full /api/v1/regulatory/* prefix; baseUrl is
 * empty. The Vite proxy (vite.config.ts) rewrites /api/v1/regulatory/*
 * to /api/admin/regulatory-readiness/* on platform-admin-api (port 9302).
 * The nginx.conf does the same rewrite in Docker/production.
 */
function createAdminRegulatoryReadinessClient() {
  const client = createClient<RegulatoryReadinessPaths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * UCC Filing Lifecycle client (aaim-core ucc-filing, container port 9554).
 * Canonical baseUrl /api/v1/ucc-filing: the admin.aaim.com LB routes
 * /api/v1/ucc-filing/* directly to the ucc-filing Cloud Run service with NO
 * pathPrefixRewrite (api-route-layer-consistency.md). The service mounts its
 * routers under /api/v1/ucc-filing, so baseUrl + spec path /ucc-filings resolves
 * to /api/v1/ucc-filing/ucc-filings.
 */
function createAdminUccFilingLifecycleClient() {
  const client = createClient<UccFilingLifecyclePaths>({ baseUrl: '/api/v1/ucc-filing', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * UCC Jurisdiction Rules client (aaim-core jurisdiction-rules, container port 9553).
 * Canonical baseUrl /api/v1/ucc-jurisdiction: the LB routes
 * /api/v1/ucc-jurisdiction/* directly to the jurisdiction-rules Cloud Run
 * service (no rewrite). baseUrl + spec path /jurisdictions resolves to
 * /api/v1/ucc-jurisdiction/jurisdictions.
 */
function createAdminUccJurisdictionRulesClient() {
  const client = createClient<UccJurisdictionRulesPaths>({ baseUrl: '/api/v1/ucc-jurisdiction', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * UCC Fee Reconciliation client (aaim-core ucc-fee-reconciliation, container port 9557).
 * Canonical baseUrl /api/v1/ucc-fees: the LB routes /api/v1/ucc-fees/* directly
 * to the ucc-fee-reconciliation Cloud Run service (no rewrite). baseUrl + spec
 * path /fee-payments/by-tenant resolves to /api/v1/ucc-fees/fee-payments/by-tenant.
 */
function createAdminUccFeeReconciliationClient() {
  const client = createClient<UccFeeReconciliationPaths>({ baseUrl: '/api/v1/ucc-fees', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * FI Staff Lifecycle client (Sprint 5 S5-A).
 * Spec paths declare the full /api/v1/admin/staff/* prefix per the
 * api-version-canonical mandate, so baseUrl is empty. The Vite proxy and
 * Docker nginx.conf forward /api/v1/admin/staff/* to platform-admin-api
 * (port 9302), which proxies to aaim-core authorization (port 9551).
 *
 * Endpoints: invitations (list, invite, revoke, resend, accept), staff
 * (list, get, suspend, reactivate, offboard, updateRoles, preview).
 * Sensitive operations (suspend, offboard, fi_admin grant) require an
 * X-Step-Up-Token header; the hook layer surfaces step-up dialogs and
 * sets the header on the request.
 */
function createAdminFiStaffLifecycleClient() {
  const client = createClient<FiStaffLifecyclePaths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Auth Step-Up client (Sprint 6 of FI Staff Authn/Z arc).
 *
 * Used by `StepUpAuthModal` to mint short-lived JWTs bound to a single
 * sensitive operation. Spec path is the canonical `/api/v1/auth/step-up-
 * token`; baseUrl is empty so the spec-declared full path routes through
 * the Vite proxy + Docker nginx.conf to platform-admin-api (port 9302),
 * which proxies to aaim-core authorization (port 9551).
 */
function createAuthStepUpClient() {
  const client = createClient<AuthStepUpPaths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Auth TOTP / Invitation-Accept client (Wave 3.5 of pilot-operator-auth
 * arc, 2026-05-27).
 *
 * Carries the anonymous /api/v1/auth/invitations/accept endpoint (the
 * canonical credential-path invitation-accept mount per ADR-008) along
 * with the TOTP enroll / verify / challenge endpoints declared in the
 * same spec. Spec paths declare the full /api/v1/auth/* prefix, so
 * baseUrl is empty. The Vite proxy + Docker nginx.conf forward
 * /api/v1/auth/* to platform-admin-api (port 9302) which proxies to
 * aaim-core authorization (port 9551).
 *
 * The accept-invitation endpoint is anonymous (token in body is the
 * authentication proof); other endpoints in the spec carry their own
 * intermediate-token Bearer credential. authMiddleware injects the
 * platform-admin Bearer harmlessly on /invitations/accept (the server
 * ignores it on the anonymous mount).
 */
function createAuthTotpClient() {
  const client = createClient<AuthTotpPaths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * User Management RBAC client (Sprint 5 S5-A).
 * Carries the IdP-bindings pending-claims, role-mappings, approve/deny,
 * and claim-mapping-overrides paths under /api/v1/admin/idp-bindings/*.
 * Spec also declares legacy /users, /organizations, /api-keys paths
 * (consumed by other panels). Spec paths are full /api/v1/admin/*; the
 * legacy /users-* paths use bare prefixes consumed elsewhere.
 *
 * baseUrl is empty so the spec-declared full paths route through the
 * Vite proxy to platform-admin-api which forwards to aaim-core
 * authorization service (port 9551).
 */
function createAdminUserManagementRbacClient() {
  const client = createClient<UserManagementRbacPaths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * ABAC Policies client (Sprint 5 S5-A).
 * Spec paths declare the full /api/v1/admin/abac-policies/*, /api/v1/
 * admin/roles/{roleId}/abac-policies/*, /api/v1/admin/staff/{userId}/
 * role-abac-overrides, and /api/v1/admin/abac/check prefixes per the
 * api-version-canonical mandate. baseUrl empty; Vite proxy and Docker
 * nginx route to platform-admin-api which forwards to aaim-core
 * authorization (port 9551).
 *
 * Sprint 1 deviation honored: AbacPolicy has no displayName field;
 * `name` serves as both slug and label.
 */
function createAdminAbacPoliciesClient() {
  const client = createClient<AbacPoliciesPaths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Support Platform client (Sprint W2d -- arc-followon-1 S5).
 *
 * Carries both platform-admin queue routes (`/api/v1/support/cases/*`,
 * `/agents`, `/sla`, `/aaim-e-handoff`) and FI-operator self-case routes
 * (`/api/v1/support/fi-cases/*`). Same spec, two audiences -- audience
 * selection is performed in the page components via the auth store's
 * `hasAnyRole` helper. The server-side resolveEffectiveTenantId enforces
 * scope: consumer cases at apex, FI-operator cases at the FI tenant id,
 * platform-admin sees both via apex resolution.
 *
 * Spec paths declare the full `/api/v1/support/*` prefix per the
 * api-version-canonical mandate, so baseUrl is empty. The Vite proxy and
 * Docker nginx.conf forward `/api/v1/support/*` to the aaim-core support
 * service (port 9564 per Sprint W1d scaffold).
 */
function createSupportPlatformClient() {
  const client = createClient<SupportPlatformPaths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Examiner Evidence Package client (GH #1930, examiner-evidence arc node 9).
 *
 * Spec paths declare the full /api/admin/examiners/evidence-package[/manifest]
 * prefix, so baseUrl is empty. The Vite catch-all /api proxy forwards to
 * platform-admin-api (port 9302), which mounts both POSTs behind
 * requireAuthenticatedBearer (src/server.ts:731). In production the
 * admin.aaim.com LB routes /api/admin/* directly to platform-admin-api.
 *
 * The ZIP download POST is consumed with parseAs: 'blob' in
 * useExaminerEvidence; the manifest POST returns JSON
 * (EvidencePackageManifest). Both endpoints fail closed with
 * 502 CORE_EVIDENCE_UNAVAILABLE when the UCC filing upstream is configured
 * but unreachable -- the hook layer surfaces that as a distinct error state.
 */
function createExaminerEvidenceClient() {
  const client = createClient<ExaminerEvidencePackagePaths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/**
 * Member Liquidity Engine client (overdraft-coverage arc OC-3). Spec paths
 * declare the full /api/v1/tempo/* prefix, so the factory's default
 * baseUrl is '' (same-origin). The Vite proxy forwards /api/v1/tempo/*
 * to port 9575 (see vite.config.ts) without rewrite, and the Docker
 * nginx.conf mirrors it -- the loanSyndicationClient pattern. The whole
 * surface is gated server-side on Institution.enableOverdraftPreview
 * (404-on-OFF), so consumers treat 404 as "surface dark", not an error.
 */
function createAdminMemberLiquidityClient() {
  const client = createMemberLiquidityClient();
  client.use(authMiddleware);
  return client;
}

/**
 * Economic Modeling client (strategy-tools-api 9385, GH #2727). Spec paths
 * declare the full /api/v1/modeling/* prefix, so baseUrl is '' (same-origin)
 * and the Vite proxy / nginx forwards it -- the memberLiquidityClient pattern.
 * The surface is gated server-side on Institution.enableOverdraftPreview
 * (403-on-OFF), so consumers treat 403 as "surface dark", not an error.
 */
function createAdminEconomicModelingClient() {
  const client = createEconomicModelingClient('');
  client.use(authMiddleware);
  return client;
}

/**
 * Core deposit feed client (tempo-engine 9575, GH #2911). Spec paths declare
 * the full /api/v1/core-deposit-feed/* prefix, so baseUrl is '' (same-origin)
 * and the Vite proxy / nginx forward the prefix to tempo-engine without
 * rewrite -- the memberLiquidityClient pattern. The router sits behind the
 * same Institution.enableOverdraftPreview gate as /api/v1/tempo (404-on-OFF),
 * so consumers treat 404 as "surface dark or not found", not an error.
 */
function createAdminCoreDepositFeedClient() {
  const client = createClient<CoreDepositFeedPaths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

/** Singleton instances -- created once, reused across service files */
export const platformAdminClient = createAdminPlatformClient();
export const platformAdminCanonicalClient = createAdminPlatformCanonicalClient();
export const identityProviderClient = createAdminIdentityProviderClient();
export const auditTrailClient = createAdminAuditClient();
export const propertyTitleAdminClient = createPropertyTitleAdminClient();
export const providerRateLimitsClient = createAdminProviderRateLimitsClient();
export const complianceClient = createAdminComplianceClient();
export const economicModelingClient = createAdminEconomicModelingClient();
export const tenantWebhookConfigClient = createAdminTenantWebhookConfigClient();
export const webhookDeliveryLogClient = createAdminWebhookDeliveryLogClient();
export const loanSyndicationClient = createAdminLoanSyndicationClient();
export const cooperativeReserveTokenClient = createAdminCooperativeReserveTokenClient();
export const regulatoryReadinessClient = createAdminRegulatoryReadinessClient();
export const uccFilingLifecycleClient = createAdminUccFilingLifecycleClient();
export const uccJurisdictionRulesClient = createAdminUccJurisdictionRulesClient();
export const uccFeeReconciliationClient = createAdminUccFeeReconciliationClient();
export const fiStaffLifecycleClient = createAdminFiStaffLifecycleClient();
export const authStepUpClient = createAuthStepUpClient();
export const authTotpClient = createAuthTotpClient();
export const userManagementRbacClient = createAdminUserManagementRbacClient();
export const abacPoliciesClient = createAdminAbacPoliciesClient();
export const supportPlatformClient = createSupportPlatformClient();
export const examinerEvidenceClient = createExaminerEvidenceClient();
export const memberLiquidityClient = createAdminMemberLiquidityClient();
export const coreDepositFeedClient = createAdminCoreDepositFeedClient();

// ---------------------------------------------------------------------------
// Legacy ApiClient for non-spec'd endpoints
// ---------------------------------------------------------------------------

/**
 * API error class (retained for backward compatibility with page components
 * that catch ApiError instances).
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown
  ) {
    super(`API Error: ${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

/**
 * Legacy API client for endpoints without OpenAPI spec coverage.
 *
 * Used by: dashboard, loans, loan-pipeline, risk-dashboard services
 * and page components with non-spec'd endpoints (members, rbac).
 *
 * All new endpoints should be added to the canonical OpenAPI specs and
 * consumed via the typed clients above.
 *
 * DEPRECATED (follow-on C13->C14-3): every consumer of this class should
 * be migrated to the typed `platformAdminClient` (or sibling) once the
 * relevant endpoints are added to the canonical OpenAPI specs. Until then
 * this wrapper carries the same auth/tenant injection responsibilities as
 * `authMiddleware` so multi-tenant calls (e.g. lending-platform's
 * `extractTenantPreHandler` which requires `X-Tenant-ID`) succeed.
 */
export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = '/api') {
    this.baseUrl = baseUrl;
  }

  /**
   * Build the per-request auth headers from the auth store.
   *
   * Sprint C13 WP-3 (2026-04-30): we inject `X-Tenant-ID` whenever the
   * store has a non-null `user.tenantId`. The lending-platform Fastify
   * pre-handler at `services/lending-platform/src/server.ts:105-120`
   * (extractTenantPreHandler) requires this header on every request and
   * returns 400 MISSING_TENANT if absent. Without the injection, the
   * admin portal's lending-config page hit 400 on every load even with
   * a valid Bearer.
   *
   * The header is omitted when `tenantId` is null / undefined so that
   * non-tenant-scoped endpoints (which would reject an unsolicited
   * tenant header in some implementations) are not affected.
   *
   * Reading the auth store via `useAuthStore.getState()` rather than the
   * React hook is intentional -- this code runs outside any component
   * and we want the latest persisted state on every request, not a
   * snapshot bound to a render cycle.
   */
  private getAuthHeader(): HeadersInit {
    const state = useAuthStore.getState();
    const headers: Record<string, string> = {};
    // No Authorization header (N6, SEC-V04): the HttpOnly session cookie -- sent
    // via credentials: 'include' on every request below -- authenticates the
    // call. We still inject X-Tenant-ID for tenant-scoped peers.
    const tenantId = state.user?.tenantId;
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    } else if (state.user) {
      headers['X-Tenant-ID'] = '00000000-0000-0000-0000-000000aaaaaa';
    }
    return headers;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    // CSRF double-submit (N8): echo the csrf-token cookie as X-CSRF-Token on
    // mutating methods, mirroring the openapi-fetch authMiddleware.onRequest.
    // Built per attempt: the silent refresh below re-mints the cookie, so the
    // retry echoes the value current after it, as authMiddleware's retry
    // does (GH #4888).
    const method = (options.method ?? 'GET').toUpperCase();
    const buildHeaders = () => ({
      'Content-Type': 'application/json',
      ...this.getAuthHeader(),
      ...(CSRF_MUTATING_METHODS.has(method) ? csrfHeaders() : {}),
      ...options.headers,
    });

    let response = await fetch(url, {
      ...options,
      headers: buildHeaders(),
      credentials: 'include',
    });

    // Cookie-based silent-refresh + single retry on 401 (mirrors
    // authMiddleware). Keeps the legacy ApiClient surfaces (dashboard, loans,
    // etc.) on the same transparent-refresh behavior as the openapi-fetch
    // clients. The retry reuses credentials: 'include' so the rotated session
    // cookie rides it; no bearer is attached.
    if (response.status === 401) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        response = await fetch(url, {
          ...options,
          headers: buildHeaders(),
          credentials: 'include',
        });
      }
    }

    if (!response.ok) {
      if (await isAuthBrokenResponse(response)) {
        bounceToLogin();
        throw new ApiError(
          response.status,
          response.status === 401 ? 'Unauthorized - Please login again' : 'Auth state broken - redirecting to login',
        );
      }

      const errorData = await response.json().catch(() => null);
      throw new ApiError(response.status, response.statusText, errorData);
    }

    if (response.status === 204) {
      return null as T;
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

/**
 * Singleton legacy API client for non-spec'd endpoints.
 * Prefer typed clients (platformAdminClient, auditTrailClient, etc.) for spec'd paths.
 */
export const apiClient = new ApiClient();
